import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { Formik } from 'formik'

import { getUnprocessableErrors, isUserBlockedError } from 'utils/error'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { schema } from './schema'
import styled from 'styled-components'
import { ApolloError } from '@apollo/client'
import { ILoginFormValues } from 'features/Login/types'
import { InputPassword } from 'components/InputPassword'

const Form = styled.form`
  max-width: 350px;
  width: 100%;
  margin: auto;
`

const InputWrapper = styled.div`
  margin-top: 10px;
  position: relative;

  &:first-child {
    margin-top: 0;
  }
`

const ButtonWrapper = styled.div`
  margin-top: 20px;
`

const ErrorMessageWrapper = styled.div`
  margin-bottom: 5px;
`

const initialValues = {
  username: '',
  password: ''
}
const messages = defineMessages({
  usernamePlaceholder: {
    id: 'login.username.placeholder',
    defaultMessage: 'Username'
  },
  passwordPlaceholder: {
    id: 'login.password.placeholder',
    defaultMessage: 'Password'
  }
})

interface ILoginFormProps {
  error?: ApolloError
  loading: boolean
  handleSubmit: (values: ILoginFormValues) => void
}
const LoginForm = ({ error, loading, handleSubmit }: ILoginFormProps): ReactElement => {
  const intl = useIntl()

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
      {(props): ReactElement => {
        const { touched, errors, handleChange, handleBlur, handleSubmit } = props

        return (
          <Form onSubmit={handleSubmit}>
            <InputWrapper>
              <Input
                id="username"
                value={props.values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.usernamePlaceholder)}
                touched={touched.username}
                errors={errors.username}
              />
            </InputWrapper>
            <InputWrapper>
              <InputPassword
                id="password"
                value={props.values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.passwordPlaceholder)}
                touched={touched.password}
                errors={errors.password}
              />
            </InputWrapper>
            <ButtonWrapper>
              {error ? (
                <ErrorMessageWrapper>
                  {isUserBlockedError(error) ? (
                    <ErrorMessage>
                      <FormattedMessage
                        id="login.submit.error.blocked"
                        defaultMessage="Your account has been blocked. Please contact the system administrator."
                      />
                    </ErrorMessage>
                  ) : (
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage id="login.submit.error" defaultMessage="Username or password incorrect" />
                    </ErrorMessage>
                  )}
                </ErrorMessageWrapper>
              ) : null}
              <Button type="submit" kind="primary" loading={loading} fullWidth>
                <FormattedMessage id="dictionary.signin" defaultMessage="Sign in" />
              </Button>
            </ButtonWrapper>
          </Form>
        )
      }}
    </Formik>
  )
}

export default LoginForm
